/**
 * Turning a slider position into a value, and back.
 *
 * A slider over a column of resistances has to cross seven orders of magnitude:
 * this catalogue holds parts from a fraction of an ohm to ten megohms. Spread
 * linearly, everything below a hundred kilohms sits inside the first pixel and
 * the whole range of parts anybody actually filters for is unreachable. So a
 * wide span is travelled logarithmically — the handle moves by *ratio*, which
 * is how these values are chosen in the first place: the step from 1k to 10k
 * matters as much as the one from 100k to 1M.
 *
 * A narrow span — a tolerance from 1 to 5 per cent, a count from 0 to 40 — is
 * linear, because on those the ratio means nothing.
 */

/** Slider positions are integers; this is how many the track has. */
export const STEPS = 1000;

/** Whether this span is wide enough to be worth a logarithmic scale.
 *
 *  Three decades is the threshold, and a span touching zero cannot be
 *  logarithmic at all — the logarithm of nothing is nowhere. */
export function isWide(low: number, high: number): boolean {
  return low > 0 && high / low >= 1000;
}

/** Where on the track this value sits, 0…STEPS. */
export function toPosition(value: number, low: number, high: number): number {
  if (!(high > low)) return 0;
  const clamped = Math.min(Math.max(value, low), high);
  const fraction = isWide(low, high)
    ? (Math.log(clamped) - Math.log(low)) / (Math.log(high) - Math.log(low))
    : (clamped - low) / (high - low);
  return Math.round(fraction * STEPS);
}

/** The value that position stands for. */
export function fromPosition(position: number, low: number, high: number): number {
  if (!(high > low)) return low;
  const fraction = Math.min(Math.max(position, 0), STEPS) / STEPS;
  return isWide(low, high)
    ? Math.exp(Math.log(low) + fraction * (Math.log(high) - Math.log(low)))
    : low + fraction * (high - low);
}

const PREFIXES: [number, string][] = [
  [1e9, 'G'],
  [1e6, 'M'],
  [1e3, 'k'],
  [1, ''],
  [1e-3, 'm'],
  [1e-6, 'µ'],
  [1e-9, 'n'],
  [1e-12, 'p'],
];

/**
 * The number as it is written on the part: `4.7k`, `100n`, `0.125`.
 *
 * Three significant figures, because that is as fine as any of these parts are
 * made and as fine as a slider can be aimed. Trailing zeros go: `10k`, never
 * `10.0k`.
 */
export function engineering(value: number, unit = ''): string {
  if (!Number.isFinite(value)) return '';
  if (value === 0) return `0${unit}`;

  const magnitude = Math.abs(value);
  const [scale, prefix] = PREFIXES.find(([size]) => magnitude >= size) ?? [1e-12, 'p'];
  const scaled = value / scale;
  const digits = Math.abs(scaled) >= 100 ? 0 : Math.abs(scaled) >= 10 ? 1 : 2;

  return `${Number(scaled.toFixed(digits))}${prefix}${unit}`;
}

const FACTORS: Record<string, number> = {
  G: 1e9,
  M: 1e6,
  k: 1e3,
  K: 1e3,
  m: 1e-3,
  µ: 1e-6,
  u: 1e-6,
  n: 1e-9,
  p: 1e-12,
};

/**
 * A written value back to a number: `4k7`, `4.7k`, `100n`, `0.125W`, `10MΩ`.
 *
 * The prefix standing in for the decimal point — `4k7` — is how these are
 * marked on tape and written on a whiteboard, and the one this catalogue's own
 * hint tells people they may use. Anything unreadable is `null` rather than a
 * guess: the slider then simply does not follow, which is better than jumping
 * somewhere while somebody is still typing.
 */
export function parseEngineering(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (!trimmed) return null;

  // Prefix between the digits: 4k7 is 4.7k.
  const between = /^(\d+)([GMkKmµunp])(\d+)$/.exec(trimmed);
  if (between) {
    return Number(`${between[1]}.${between[3]}`) * FACTORS[between[2]!]!;
  }

  const plain = /^(-?\d*\.?\d+)\s*([GMkKmµunp])?/.exec(trimmed);
  if (!plain) return null;

  const scale = plain[2] ? FACTORS[plain[2]] : 1;
  const value = Number(plain[1]) * (scale ?? 1);
  return Number.isFinite(value) ? value : null;
}

/**
 * The unit at the end of a written value: `10MΩ` → `Ω`, `0.125W` → `W`, `40` →
 * nothing.
 *
 * Taken from the column's own bounds rather than from a table of units per
 * column: the column already knows how it writes itself, and a second list to
 * keep in step with the first is a list that falls out of step.
 */
export function unitOf(written: string): string {
  const tail = /(\s*)([^\d\s.,+-]+)$/.exec(written.trim());
  if (!tail) return '';

  // The SI prefix belongs to the number, not to the unit: in "10MΩ" the unit is
  // Ω, and keeping the M would multiply everything by a million on the way back.
  //
  // Only when it is written against the number, though. In "100 ppm/°C" the
  // space says the unit starts there, and stripping a prefix would turn it into
  // "pm/°C" — picometres per degree.
  return tail[1] ? tail[2]! : tail[2]!.replace(/^[GMkmµunp]/, '');
}
